(function () {
  "use strict";

  var FALLBACK_LOGO_URL = "https://cdn.shopify.com/s/files/1/0920/9082/7081/files/logo-Photoroom.png?v=1737634672";
  var ICON_COLOR = 0x141414;
  var PLANE_H = 1.5; // world units - keep in sync with the camera framing below

  function initScene(container) {
    if (!container || container.dataset.injuryLogo3dInit === "true") return;
    container.dataset.injuryLogo3dInit = "true";

    var logoUrl = container.dataset.logoUrl || FALLBACK_LOGO_URL;
    var reduceMotion = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    var width = container.clientWidth || 100;
    var height = container.clientHeight || 100;

    var scene = new THREE.Scene();
    var camera = new THREE.PerspectiveCamera(35, width / height, 0.1, 100);
    camera.position.z = 5;

    var renderer;
    try {
      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    } catch (e) {
      return; // WebGL not available, leave the noscript/fallback in place
    }
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setSize(width, height);
    renderer.setClearColor(0x000000, 0);
    container.appendChild(renderer.domElement);

    var light1 = new THREE.DirectionalLight(0xffffff, 1);
    light1.position.set(2, 3, 4);
    scene.add(light1);
    var light2 = new THREE.DirectionalLight(0xffffff, 0.4);
    light2.position.set(-2, -1, 2);
    scene.add(light2);
    scene.add(new THREE.AmbientLight(0xffffff, 0.55));

    // ---------- silhouette -> solid geometry ----------
    // Traces the PNG's alpha silhouette (foreground shapes + their enclosed holes) into
    // real 2D paths, then extrudes them into one continuous solid mesh - so the icon has
    // genuine thickness with a single unbroken edge, following only the icon's own shape.

    function buildExtrudedIcon(image) {
      var scaleUp = 3;
      var W = Math.max(1, Math.round(image.width * scaleUp));
      var H = Math.max(1, Math.round(image.height * scaleUp));
      var canvas = document.createElement("canvas");
      canvas.width = W;
      canvas.height = H;
      var ctx = canvas.getContext("2d");
      ctx.imageSmoothingEnabled = true;
      ctx.drawImage(image, 0, 0, W, H);
      var data = ctx.getImageData(0, 0, W, H).data;

      var threshold = 120;
      function isFG(x, y) {
        if (x < 0 || y < 0 || x >= W || y >= H) return false;
        return data[(y * W + x) * 4 + 3] > threshold;
      }

      // --- connected-component labeling: foreground blobs ---
      var fgLabels = new Int32Array(W * H);
      var fgList = [];
      var fgCount = 0;
      for (var y0 = 0; y0 < H; y0++) {
        for (var x0 = 0; x0 < W; x0++) {
          var i0 = y0 * W + x0;
          if (isFG(x0, y0) && fgLabels[i0] === 0) {
            fgCount++;
            var label = fgCount;
            fgLabels[i0] = label;
            var stack = [x0, y0];
            while (stack.length) {
              var py = stack.pop();
              var px = stack.pop();
              var cand = [px + 1, py, px - 1, py, px, py + 1, px, py - 1];
              for (var c = 0; c < 8; c += 2) {
                var nx = cand[c],
                  ny = cand[c + 1];
                if (nx >= 0 && ny >= 0 && nx < W && ny < H) {
                  var ni = ny * W + nx;
                  if (isFG(nx, ny) && fgLabels[ni] === 0) {
                    fgLabels[ni] = label;
                    stack.push(nx, ny);
                  }
                }
              }
            }
            fgList.push({ label: label, startX: x0, startY: y0 });
          }
        }
      }
      if (!fgCount) return null;

      // --- connected-component labeling: background blobs (to find enclosed holes) ---
      var bgLabels = new Int32Array(W * H);
      var bgCount = 0;
      var holes = [];
      for (var y1 = 0; y1 < H; y1++) {
        for (var x1 = 0; x1 < W; x1++) {
          var i1 = y1 * W + x1;
          if (!isFG(x1, y1) && bgLabels[i1] === 0) {
            bgCount++;
            var blabel = bgCount;
            bgLabels[i1] = blabel;
            var stack2 = [x1, y1];
            var touchesBorder = false;
            var adjacentFg = 0;
            while (stack2.length) {
              var py2 = stack2.pop();
              var px2 = stack2.pop();
              if (px2 === 0 || py2 === 0 || px2 === W - 1 || py2 === H - 1) touchesBorder = true;
              var cand2 = [px2 + 1, py2, px2 - 1, py2, px2, py2 + 1, px2, py2 - 1];
              for (var c2 = 0; c2 < 8; c2 += 2) {
                var nx2 = cand2[c2],
                  ny2 = cand2[c2 + 1];
                if (nx2 >= 0 && ny2 >= 0 && nx2 < W && ny2 < H) {
                  var ni2 = ny2 * W + nx2;
                  if (isFG(nx2, ny2)) {
                    if (!adjacentFg) adjacentFg = fgLabels[ni2];
                  } else if (bgLabels[ni2] === 0) {
                    bgLabels[ni2] = blabel;
                    stack2.push(nx2, ny2);
                  }
                }
              }
            }
            if (!touchesBorder && adjacentFg) {
              holes.push({ startX: x1, startY: y1, bgLabel: blabel, fgLabel: adjacentFg });
            }
          }
        }
      }

      // --- Moore-neighbor boundary tracing ---
      var DIRS = [
        [-1, 0],
        [-1, -1],
        [0, -1],
        [1, -1],
        [1, 0],
        [1, 1],
        [0, 1],
        [-1, 1],
      ];
      function dirIndex(dx, dy) {
        for (var d = 0; d < 8; d++) if (DIRS[d][0] === dx && DIRS[d][1] === dy) return d;
        return 0;
      }
      function traceBoundary(belongsTo, sx, sy) {
        var points = [];
        var curr = [sx, sy];
        var back = [sx - 1, sy];
        var maxSteps = Math.min(W * H, 6000);
        var steps = 0;
        do {
          points.push(curr);
          var dx = back[0] - curr[0],
            dy = back[1] - curr[1];
          var startDir = dirIndex(dx, dy);
          var found = false;
          for (var k = 1; k <= 8; k++) {
            var di = (startDir + k) % 8;
            var nx = curr[0] + DIRS[di][0],
              ny = curr[1] + DIRS[di][1];
            if (belongsTo(nx, ny)) {
              back = curr;
              curr = [nx, ny];
              found = true;
              break;
            }
          }
          if (!found) break;
          steps++;
        } while (!(curr[0] === sx && curr[1] === sy) && steps < maxSteps);
        return points;
      }

      function toLocal(pt) {
        var sf = PLANE_H / H;
        return [(pt[0] - W / 2) * sf, (H / 2 - pt[1]) * sf];
      }
      function signedArea(pts) {
        var a = 0;
        for (var i = 0; i < pts.length; i++) {
          var p1 = pts[i],
            p2 = pts[(i + 1) % pts.length];
          a += p1[0] * p2[1] - p2[0] * p1[1];
        }
        return a / 2;
      }

      var shapes = [];
      for (var fb = 0; fb < fgList.length; fb++) {
        var info = fgList[fb];
        (function (info) {
          var raw = traceBoundary(function (x, y) {
            return x >= 0 && y >= 0 && x < W && y < H && fgLabels[y * W + x] === info.label;
          }, info.startX, info.startY);
          if (raw.length < 15) return; // discard speckle noise
          var localPts = raw.map(toLocal);
          if (signedArea(localPts) < 0) localPts.reverse();
          var shape = new THREE.Shape();
          shape.moveTo(localPts[0][0], localPts[0][1]);
          for (var p = 1; p < localPts.length; p++) shape.lineTo(localPts[p][0], localPts[p][1]);
          shape.closePath();
          shape.userData = { label: info.label };
          shapes.push(shape);
        })(info);
      }
      if (!shapes.length) return null;

      for (var h = 0; h < holes.length; h++) {
        var hinfo = holes[h];
        var rawH = traceBoundary(function (x, y) {
          return x >= 0 && y >= 0 && x < W && y < H && bgLabels[y * W + x] === hinfo.bgLabel;
        }, hinfo.startX, hinfo.startY);
        if (rawH.length < 15) continue;
        var localH = rawH.map(toLocal);
        if (signedArea(localH) > 0) localH.reverse();
        var path = new THREE.Path();
        path.moveTo(localH[0][0], localH[0][1]);
        for (var q = 1; q < localH.length; q++) path.lineTo(localH[q][0], localH[q][1]);
        path.closePath();
        for (var s = 0; s < shapes.length; s++) {
          if (shapes[s].userData.label === hinfo.fgLabel) {
            shapes[s].holes.push(path);
            break;
          }
        }
      }

      var depth = PLANE_H * 0.12;
      var geometry = new THREE.ExtrudeGeometry(shapes, {
        depth: depth,
        bevelEnabled: false,
        curveSegments: 1,
      });
      geometry.center();

      var material = new THREE.MeshStandardMaterial({
        color: ICON_COLOR,
        roughness: 0.55,
        metalness: 0.08,
      });
      var mesh = new THREE.Mesh(geometry, material);
      var group = new THREE.Group();
      group.add(mesh);
      return group;
    }

    function buildFlatFallback(image) {
      try {
        var tex = new THREE.Texture(image);
        tex.needsUpdate = true;
        var aspect = image.width / image.height;
        var planeW = PLANE_H * aspect;
        var geo = new THREE.PlaneGeometry(planeW, PLANE_H);
        var mat = new THREE.MeshBasicMaterial({ map: tex, transparent: true, side: THREE.DoubleSide });
        var mesh = new THREE.Mesh(geo, mat);
        var group = new THREE.Group();
        group.add(mesh);
        return group;
      } catch (e) {
        return null;
      }
    }

    var img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = function () {
      var group;
      try {
        group = buildExtrudedIcon(img);
      } catch (e) {
        group = null;
      }
      if (!group) group = buildFlatFallback(img);
      if (!group) return;

      scene.add(group);
      container.classList.add("is-ready");

      var clock = new THREE.Clock();
      var speed = reduceMotion ? 0.05 : 0.55;

      function animate() {
        requestAnimationFrame(animate);
        var t = clock.getElapsedTime();
        group.rotation.y = t * speed;
        group.rotation.x = Math.sin(t * 0.4) * 0.1;
        renderer.render(scene, camera);
      }
      animate();
    };
    img.onerror = function () {
      container.style.display = "none";
    };
    img.src = logoUrl;
  }

  function init() {
    if (typeof THREE === "undefined") return;
    var containers = document.querySelectorAll(".injury-logo3d-header");
    containers.forEach(initScene);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
